<template>
    <li @click="$emit('click')"
        class="cursor-pointer p-16 group hover:bg-primary-5">
        <div class="flex items-stretch space-x-12">
            <div class="relative">
                <span
                    v-if="!isLastItem"
                    aria-hidden="true"
                    class="absolute top-40 left-1/2 translate-x-1/2 -ml-2 h-full border-l-2 border-primary-20"
                ></span>
                <div class="relative" v-if="userAvatar || (notification.data.user && notification.data.user.initials)">
                    <avatar
                        :image="userAvatar"
                        size="small"
                        class="ring-8 ring-white group-hover:ring-primary-5"
                    >
                        {{ notification.data.user.initials }}
                    </avatar>

                    <span v-if="avatarIcon"
                          class="absolute bottom-4 right-4 translate-x-1/2 translate-y-1/2 rounded-32 bg-white group-hover:bg-primary-5 p-4">
                        <svg-icon
                            :type="avatarIcon"
                            class="h-16 w-16 text-primary"
                        />
                    </span>
                </div>
                <div
                    v-else-if="icon"
                    class="h-36 w-36 bg-primary-20 rounded-32 flex items-center justify-center ring-8 ring-white group-hover:ring-primary-5"
                >
                    <svg-icon
                        :type="icon"
                        class="w-18 h-18 text-primary"
                    />
                </div>
            </div>
            <div class="min-w-0 w-full flex justify-between gap-12">
                <div>
                    <div
                        v-if="userName || (notification.data.interviewer && notification.data.interviewer.name) || jobName"
                        class="text-black"
                    >
                        <span class="font-semibold">
                            <template v-if="userName">
                                {{ userName }}
                            </template>
                            <template v-if="notification.interviewer && notification.interviewer.name">
                                {{ notification.interviewer.name }}
                            </template>
                            <span v-if="jobName" class="text-primary-80">
                                <template v-if="userName || (notification.interviewer && notification.interviewer.name)">
                                    -
                                </template>
                                {{ jobName }}
                            </span>
                        </span>
                    </div>
                    <div class="text-primary-80">
                        <p class="first-letter:uppercase" v-html="message"></p>
                    </div>
                    <span v-if="extra" class="alert-mention">
                        <span v-html="extra"></span>
                    </span>
                </div>
                <tooltip :content="moment(notification.created_at).locale(locale).format(datetimeFormat)"
                         position="top"
                         wrapper-class="self-start w-fit"
                >
                    <template v-if="!notification.read_at">
                        <tag no-icon class="text-nowrap">
                            {{ $t('general.new') }}
                        </tag>
                    </template>
                    <div v-else class="text-black text-nowrap">
                        {{ moment(notification.created_at).locale(locale).format('DD MMM') }}
                    </div>
                </tooltip>
            </div>
        </div>
    </li>
</template>

<script>
import Notification from '../../models/Notification';
import {mapState} from 'pinia';
import {useMainStore} from '../../state/store';
import moment from 'moment-timezone';
import Avatar from './Avatar.vue';
import SvgIcon from './SvgIcon';
import Tooltip from '../navigation/Tooltip';
import Tag from "./Tag.vue";

export default {
    components: {Tag, Tooltip, SvgIcon, Avatar},
    props: {
        notification: Notification,
        message: String,
        userName: String,
        userAvatar: String,
        applicationName: String,
        jobName: String,
        isLastItem: Boolean,
    },
    data: function () {
        return {
            moment,
        };
    },
    emits: ['click'],
    computed: {
        ...mapState(useMainStore, ['locale', 'datetimeFormat']),
        avatarIcon() {
            switch (this.notification.type.split('\\').slice(-1)[0]) {
                case 'ApplicationStageMoved':
                    return 'swap-vert';
                case 'ApplicationUpdated':
                    return 'edit';
                case 'ApplicationRejected':
                    return 'front-hand';
                case 'ApplicationCommentCreated':
                case 'UserMentioned':
                    return 'campaign-filled';
                case 'ApplicationScoreUpdated':
                    return 'star';
                case 'InvitationCreated':
                    return 'mail-filled';
                case 'InterviewCreated':
                    return 'forum-filled';
                case 'DocumentCreated':
                    return 'attach-file';
                default:
                    return null;
            }
        },
        icon() {
            switch (this.notification.type.split('\\').slice(-1)[0]) {
                case 'InboundEmailCreated':
                case 'ContactCreated':
                    return 'mail-filled';
                case 'InterviewInvitationConfirmed':
                case 'ApplicationCommentCreated':
                case 'CommentNotification':
                case 'InterviewCreated':
                    return 'forum-filled';
                case 'ApplicationCreated':
                case 'IntroduceCreated':
                    return 'applications';
                case 'ApplicationUpdated':
                    return 'edit';
                case 'ApplicationRejected':
                    return 'hand';
                case 'ApplicationScoreUpdated':
                    return 'star';
                case 'InvitationCreated':
                case 'TaskAssigned':
                case 'TaskDeadlineReached':
                    return 'task-alt';
                case 'DocumentCreated':
                    return 'attach-file';
                case 'CandidateIntroduced':
                    return 'location-city';
                case 'UserMentioned':
                    return 'campaign-filled';
                default:
                    return null;
            }
        },
        extra() {
            switch (this.notification.type.split('\\').slice(-1)[0]) {
                case 'InboundEmailCreated':
                    return this.notification.data.email.subject;

                case 'ContactCreated':
                    return this.notification.data.contact.subject;

                case 'InterviewInvitationConfirmed':
                    return this.$t('notifications.application.interviewConfirmed_subtext', {
                        date: moment.utc(this.notification.data.interview.starts_at)
                            .locale(this.locale)
                            .tz('Europe/Brussels')
                            .format('LLLL'),
                    });

                case 'TaskAssigned':
                    return this.notification.data.task.title;

                case 'TaskDeadlineReached':
                    const dateTimeMoment = moment.utc(this.notification.data.task.due_date).local().locale(this.locale);

                    return dateTimeMoment.isSame(new Date(), "day") ?
                        this.$t('notifications.task_deadline', {
                            time: `<strong>${dateTimeMoment.format('HH:mm')}</strong>`,
                        }) : this.$t('notifications.task_deadline_passed', {
                            date: `<strong>${dateTimeMoment.format('LLLL')}</strong>`
                        });
                default:
                    return null;
            }
        },
    },
};
</script>

<style scoped>
ul li b{
    @apply text-semantic-info;
}
</style>
